import { useRef, useState, useEffect } from "react";
import { animate, useInView } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { Reveal, Overline } from "./Motion";

const PAIRS = [
  { before: "/img/real/roof1_before.jpg", after: "/img/real/roof1_after.jpg", title: "Full roof replacement + new siding", place: "Good Home Improvement project — drone photos" },
];

const Slider = ({ pair }) => {
  const box = useRef(null);
  const [pos, setPos] = useState(50);
  const [drag, setDrag] = useState(false);
  const inView = useInView(box, { once: true, amount: 0.4 });

  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(50, [50, 28, 72, 50], { duration: 2, ease: "easeInOut", onUpdate: setPos });
    return () => c.stop();
  }, [inView]);

  const move = (x) => {
    const r = box.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={box}
      className="relative aspect-[4/3] cursor-ew-resize select-none overflow-hidden rounded-2xl bg-slate-200 touch-pan-y sm:aspect-[2/1]"
      onPointerDown={(e) => { setDrag(true); e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); }}
      onPointerMove={(e) => drag && move(e.clientX)}
      onPointerUp={() => setDrag(false)}
      onPointerCancel={() => setDrag(false)}
      data-testid="before-after-slider"
    >
      <img loading="lazy" decoding="async" src={pair.before} alt="Before" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <img loading="lazy" decoding="async" src={pair.after} alt="After" draggable={false} className="absolute inset-0 h-full w-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
      <span className="absolute bottom-4 right-4 rounded-md bg-slate-950/80 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-white" data-testid="before-label">Before</span>
      <div className="pointer-events-none absolute inset-y-0 w-[3px] -translate-x-1/2 bg-white shadow-[0_0_20px_rgba(0,0,0,.4)]" style={{ left: `${pos}%` }}>
        <span className={`absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-amber-500 text-slate-950 shadow-xl transition-transform duration-200 ${drag ? "scale-110" : ""}`}>
          <MoveHorizontal className="h-6 w-6" />
        </span>
      </div>
      <input
        type="range" min="0" max="100" value={Math.round(pos)} onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Drag to compare before and after" className="sr-only" data-testid="before-after-range"
      />
    </div>
  );
};

export const BeforeAfter = () => (
  <section className="bg-white py-20 md:py-28" data-testid="before-after-section">
    <div className="wrap">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>See the difference</Overline>
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Before & after</h2>
        </div>
        <p className="max-w-sm text-sm text-slate-600">Drag the handle to see one of our real projects, captured by our drone.</p>
      </Reveal>
      {PAIRS.map((p) => (
        <Reveal key={p.after} className="mx-auto mt-10 max-w-5xl" delay={0.1}>
          <Slider pair={p} />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-display text-xl font-bold uppercase text-slate-900">{p.title}</span>
            <span className="text-slate-500">{p.place}</span>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
